/**
 * The puff a bus makes when its ice count drops but the shell survives.
 *
 * `Bus.DecreaseIceCount` (`__0x20fb6b0.c`:59-81) plays `iceCountdownParticle`
 * (0x88) with `withChildren = true`, which fires four systems at once: the
 * `IceCount` snowflakes and, alongside them, `Glow`, `Smoke` and `Snow`. None is
 * a sub-emitter — `SubModule.enabled` is 0 on all four — so this is four
 * independent one-shot bursts sharing an origin and a start time. The whole
 * spec, with the numbers, is `ICE_COUNT_VFX` in `tuning.ts`.
 *
 * Simulation only. Nothing here knows about materials, billboards or the frame
 * — `sessionView` reads these positions and draws them, the same split
 * `iceBurst.ts` uses for the break shards.
 *
 * Where a particle is born and which way it goes is the shape module, and three
 * of the four are a **Box** (`type: 5`) the size of the bus's footprint: a
 * sprite starts anywhere in that volume and leaves along the system's +Z, which
 * is the bus's forward — every particle of a system travels parallel. The
 * fourth, `Glow`, has its shape module off and is born at the origin. This
 * used to be a 25° cone about +Y for all three; that was the test reading
 * Unity's `ShapeType` wrong (FRZ-3).
 *
 * What else the prefab switches on, and this models (FRZ-7):
 *
 *  - **Hierarchy scaling** (`scalingMode: 0`): `IceCount`'s `m_LocalScale`
 *    multiplies `startSize` and `startSpeed` as well as the box. 📐 Unity's
 *    `ParticleSystemScalingMode.Hierarchy` — the same number the shape already
 *    took.
 *  - **`gravityModifier`**: 0.2 on `IceCount`, 1 on `Snow`, 0 on the other
 *    two. A multiplier on `Physics.gravity`, applied to the velocity each step
 *    (semi-implicit Euler, as `advanceIceBurst`). ❓ Whether Unity scales
 *    gravity by the hierarchy scale as well is not something the kit can
 *    settle; the port leaves it unscaled (checklist §10).
 *  - **`startRotation`** and **`RotationModule`**: an angle about the sprite's
 *    facing axis (`m_RenderAlignment: 0` View, `rotation3D: 0`). `Glow`,
 *    `Smoke` and `Snow` are born at a random angle; `IceCount` and `Smoke`
 *    then spin at a rate that is RandomBetweenTwoCurves — the blend between
 *    the two curves is drawn **once** at birth and kept, which is what Unity's
 *    per-particle random does. ❓ The sign convention (which way a positive
 *    rate turns the sprite on screen) follows `busParticles`' `spin`; the
 *    curves cross zero either way, so it is not observable here.
 *  - **`SizeModule`** on `IceCount`, `Glow`, `Snow`: `startSize × curve(age)`.
 *  - **`ColorModule`** on all four: a gradient's colour and alpha at `age`.
 *
 * And the rest of what is switched on (FRZ-9):
 *
 *  - **`ClampVelocityModule`** on all four — a speed limit (`separateAxis: 0`)
 *    the excess over which is damped by `dampen` **per frame**. Unity's rule is
 *    native; the port uses `advanceParticles`' reading of it, normalised to
 *    60 Hz so the result does not depend on the frame rate (📐, a deliberate
 *    difference — see `busParticles.ts`). The limit is multiplied by
 *    `hierarchyScale` like `startSpeed`: ❓ §10, with the gravity question.
 *    This is the module that decides what the puff looks like — 5–10 u/s
 *    snowflakes are pulled to ≤ 1 u/s within a dozen frames, so the burst
 *    hangs over the roof rather than streaking off it.
 *  - **`CollisionModule`** on `IceCount` only, World, against the ground plane
 *    y = 0 with the same split `advanceIceBurst` uses (❓ §10). It cannot fire
 *    from a bus on the ground — see `ICE_COUNT_VFX.parent.collision` — but it
 *    is the prefab's rule and it costs one comparison.
 *  - **`UVModule`** on `Smoke`: one of the 2×2 cells of `cloud_2x2_soft`,
 *    chosen at birth (`frameOverTime` RandomBetweenTwoConstants 0..0.9999).
 *    Before this the whole four-cloud sheet was drawn on every quad.
 *  - **Renderer `m_Pivot.x 0.4`** on `IceCount`: `puffSpriteMatrix` shifts the
 *    quad by 0.4 of its width along its own x before the spin, so the
 *    snowflake swings about an off-centre point (❓ sign, §10).
 *  - `CustomDataModule` is `enabled: 1` on `IceCount` with both streams'
 *    `mode` **Disabled** and no custom vertex stream on the renderer — a
 *    no-op, not ported.
 *
 * ⚠️ This header used to claim `gravityModifier` was 0 on all four. It never
 * was; FRZ-3 corrected the words and FRZ-7 the motion.
 */
import { Matrix4, type Quaternion, Vector3 } from 'three'
import type { BoxSizeEnum } from '../core/enums'
import { ICE_COUNT_VFX } from '../game/tuning'
import { UNITY_GRAVITY, hash01 } from './iceBurst'
import { curveAt, gradientAt } from './unityCurve'

/** One sprite, alive from `lifetime` down to 0. */
export interface Puff {
  position: Vector3
  /**
   * `startSpeed × hierarchyScale` along the emit axis at birth, plus whatever
   * gravity has added since.
   */
  velocity: Vector3
  /** Seconds it has left. */
  remaining: number
  /** Seconds it started with, so a view can work out how far through it is. */
  lifetime: number
  /** `startSize × hierarchyScale`, fixed at birth. */
  startSize: number
  /** `startSize × SizeModule.curve(age)` — what to draw it at now. */
  size: number
  /** Radians about the sprite's facing axis, right now. Starts at `startRotation`. */
  rotation: number
  /**
   * Where between `RotationModule`'s `min` and `max` curve this particle's
   * rate sits, in [0, 1). Drawn once at birth. Unused on a system whose
   * module is off.
   */
  spinBlend: number
  /** `ColorModule` RGB for the current age, in [0, 1]. */
  tint: [number, number, number]
  /** `ColorModule` alpha for the current age. */
  alpha: number
  /** `gravityModifier`, the multiplier on `UNITY_GRAVITY`. */
  gravity: number
  /**
   * `ClampVelocityModule.magnitude × hierarchyScale`, fixed at birth like
   * `startSize` — one list holds puffs from buses of different sizes.
   */
  speedLimit: number
  /**
   * `UVModule`: which cell of the material's sheet this sprite draws, chosen
   * at birth. 0 on a system whose material is a single picture.
   */
  frame: number
  /** Which of the four systems emitted it; indexes `PUFF_SYSTEMS`. */
  system: number
}

/** `ICE_COUNT_VFX`, flattened to the order a `Puff.system` indexes. */
export const PUFF_SYSTEMS = [ICE_COUNT_VFX.parent, ...ICE_COUNT_VFX.children] as const

/** One entry of `PUFF_SYSTEMS` — the four differ in which modules they carry. */
export type PuffSystemSpec = (typeof PUFF_SYSTEMS)[number]

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

/**
 * The cluster's origin in the bus node's own frame — `ICE_COUNT_VFX.origin`
 * with Unity's z flipped, the same turn `BUS_FX_ANCHORS` gets in `sessionView`.
 */
export const PUFF_LOCAL_ORIGIN = new Vector3(
  ICE_COUNT_VFX.origin[0],
  ICE_COUNT_VFX.origin[1],
  -ICE_COUNT_VFX.origin[2],
)

/**
 * Unity's shape emits along its local +Z; the port's bus node has Unity's z
 * flipped (PORTING §1), so that axis is −Z here. The prefab's chain
 * (`ConveyorBox` → `Ice` → `IceCount`) carries no rotation, so the shape's
 * +Z is the bus's own forward.
 */
const EMIT_AXIS_LOCAL = new Vector3(0, 0, -1)

/**
 * A point inside a box of full size `scale × hierarchyScale`, centred on the
 * origin, in the bus node's frame. Uniform in the volume — `boxThickness` is 0
 * on every system, so nothing is biased towards the shell.
 *
 * The box is symmetric about its centre, so the z flip changes nothing here;
 * it lives in `PUFF_LOCAL_ORIGIN` alone.
 */
function boxPoint(
  scale: readonly [number, number, number],
  hierarchyScale: number,
  seed: number,
  salt: number,
  out: Vector3,
): Vector3 {
  return out.set(
    (hash01(seed, salt) - 0.5) * scale[0] * hierarchyScale,
    (hash01(seed, salt + 1) - 0.5) * scale[1] * hierarchyScale,
    (hash01(seed, salt + 2) - 0.5) * scale[2] * hierarchyScale,
  )
}

/** `SizeModule.curve` of one system at normalised age `t`; 1 when the module is off. */
export function puffSizeCurve(spec: PuffSystemSpec, t: number): number {
  return spec.sizeOverLifetime ? curveAt(spec.sizeOverLifetime, t) : 1
}

/**
 * `RotationModule.curve` of one system at normalised age `t`, in radians per
 * second: `lerp(min(t), max(t), blend) × multiplier`. 0 when the module is off.
 */
export function puffSpinRate(spec: PuffSystemSpec, blend: number, t: number): number {
  const rot = spec.rotationOverLifetime
  if (!rot) return 0
  return lerp(curveAt(rot.min, t), curveAt(rot.max, t), blend) * rot.multiplier
}

/** Write `size`, `tint` and `alpha` for the puff's current age. */
function refreshOverLifetime(p: Puff, spec: PuffSystemSpec): void {
  const t = p.lifetime > 0 ? 1 - p.remaining / p.lifetime : 1
  p.size = p.startSize * puffSizeCurve(spec, t)
  const c = gradientAt(spec.colourOverLifetime.colour, spec.colourOverLifetime.alpha, t)
  p.tint = c.rgb
  p.alpha = c.alpha
}

/**
 * Fire all four systems on a bus.
 *
 * `busMatrixWorld` is the bus node's world matrix; the shapes are placed in
 * its frame, so a bus parked across the lot spreads its snowflakes across
 * itself and not across the world's x/z. `size` picks the prefab's own
 * `hierarchyScale`, which the shape, `startSize` and `startSpeed` are all
 * multiplied by.
 *
 * `seed` makes it deterministic: the same bus at the same count always produces
 * the same puff, which is what lets a render check compare two runs.
 */
export function emitIceCountPuff(busMatrixWorld: Matrix4, size: BoxSizeEnum, seed: number): Puff[] {
  const out: Puff[] = []
  const hierarchyScale = ICE_COUNT_VFX.hierarchyScale[size]
  // World direction is the same for every particle of every system: the shape
  // axis has no spread, and all four share one frame.
  const direction = EMIT_AXIS_LOCAL.clone().transformDirection(busMatrixWorld)
  const local = new Vector3()

  for (let system = 0; system < PUFF_SYSTEMS.length; system++) {
    const spec = PUFF_SYSTEMS[system]
    for (let i = 0; i < spec.burstCount; i++) {
      // Each particle needs its own salt, or a system emits `burstCount`
      // identical sprites. The system index is folded in so two systems firing
      // the same particle number still differ.
      const salt = (system + 1) * 1013 + i * 7
      const lifetime = lerp(spec.lifetimeSeconds[0], spec.lifetimeSeconds[1], hash01(seed, salt))
      const speed = lerp(spec.speed[0], spec.speed[1], hash01(seed, salt + 3)) * hierarchyScale
      const startSize = lerp(spec.size[0], spec.size[1], hash01(seed, salt + 4)) * hierarchyScale
      const rotation = lerp(spec.startRotation[0], spec.startRotation[1], hash01(seed, salt + 8))
      const spinBlend = hash01(seed, salt + 9)
      // `UVModule`: `frameOverTime` is RandomBetweenTwoConstants 0..0.9999 over
      // `tilesX × tilesY` cells, so `floor(r × tiles)` is the cell, and 0.9999
      // is precisely so the last one is reachable without wrapping — the same
      // reading as `busParticles.frameOf`.
      const tiles = spec.sheet ? spec.sheet.tilesX * spec.sheet.tilesY : 1
      const frame = tiles > 1 ? Math.min(tiles - 1, Math.floor(hash01(seed, salt + 10) * tiles)) : 0

      if (spec.shape.type === 'box') {
        boxPoint(spec.shape.scale, hierarchyScale, seed, salt + 5, local)
      } else {
        // Shape module disabled: born at the origin, and — Unity's rule for a
        // shapeless system — still sent along +Z. `Glow` is the only one, at
        // speed 0, so the direction is moot until the prefab changes.
        local.set(0, 0, 0)
      }
      // `local` is scratch; each puff owns its own vector.
      const position = local.add(PUFF_LOCAL_ORIGIN).applyMatrix4(busMatrixWorld).clone()

      const puff: Puff = {
        position,
        velocity: direction.clone().multiplyScalar(speed),
        remaining: lifetime,
        lifetime,
        startSize,
        size: startSize,
        rotation,
        spinBlend,
        tint: [1, 1, 1],
        alpha: 1,
        gravity: spec.gravityModifier,
        speedLimit: puffSpeedLimit(spec, hierarchyScale),
        frame,
        system,
      }
      refreshOverLifetime(puff, spec)
      out.push(puff)
    }
  }
  return out
}

/**
 * `ClampVelocityModule`'s speed limit for one system, in world units — the
 * prefab's `magnitude × hierarchyScale`, the same scaling `startSpeed` gets.
 * ❓ That the limit scales with the hierarchy is the port's reading of
 * `ScalingMode.Hierarchy` (§10); the prefab only gives the number.
 */
export function puffSpeedLimit(spec: PuffSystemSpec, hierarchyScale: number): number {
  return spec.clampVelocity.limit * hierarchyScale
}

/**
 * Age the puff by `dt`, dropping what has expired.
 *
 * Per sprite, in this order:
 *  1. `velocity.y += gravity × dt` (semi-implicit Euler, as the break shards).
 *  2. `ClampVelocityModule`: if the speed exceeds `limit × hierarchyScale`, the
 *     **excess** is scaled by `(1 − dampen)^(dt × 60)` — Unity's per-frame
 *     `dampen`, normalised to 60 Hz exactly as `advanceParticles` does it, so
 *     the two clamps in the port agree with each other. A sprite under the
 *     limit is left alone, which is why gravity still pulls `Snow` past 0.5.
 *  3. The new velocity moves it.
 *  4. `CollisionModule` (`IceCount` only): should the step end below
 *     `groundY + radius`, the sprite is put on the plane and its velocity split
 *     at the plane's normal — normal part reflected × `bounce`, tangential
 *     part × `(1 − dampen)` — the reading `advanceIceBurst` documents (❓ §10).
 *     `radius` is `size / 2 × radiusScale`, at 0.01 under 0.01 u.
 *  5. `rotation += spinRate(age) × dt` with the rate read at the new age; then
 *     size, tint and alpha for the new age.
 *
 * `groundY` is the world height of the `Ground` collider — 0 in the shipped
 * scene; a parameter so a test can put the plane where a sprite can reach it.
 */
export function advanceIceCountPuff(puffs: Puff[], dt: number, groundY: number = 0): Puff[] {
  if (dt <= 0) return puffs
  let write = 0
  for (let i = 0; i < puffs.length; i++) {
    const p = puffs[i]
    p.remaining -= dt
    if (p.remaining <= 0) continue
    const spec = PUFF_SYSTEMS[p.system]
    p.velocity.y += UNITY_GRAVITY * p.gravity * dt

    const limit = p.speedLimit
    const speed = p.velocity.length()
    if (speed > limit) {
      const keep = Math.pow(1 - spec.clampVelocity.dampen, dt * 60)
      p.velocity.multiplyScalar((limit + (speed - limit) * keep) / speed)
    }

    p.position.addScaledVector(p.velocity, dt)

    const collision = spec.collision
    if (collision) {
      const radius = p.size * 0.5 * collision.radiusScale
      if (p.position.y < groundY + radius && p.velocity.y < 0) {
        p.position.y = groundY + radius
        const vn = p.velocity.y
        p.velocity.x *= 1 - collision.dampen
        p.velocity.z *= 1 - collision.dampen
        p.velocity.y = -vn * collision.bounce
      }
    }

    const t = 1 - p.remaining / p.lifetime
    p.rotation += puffSpinRate(spec, p.spinBlend, t) * dt
    refreshOverLifetime(p, spec)
    puffs[write++] = p
  }
  puffs.length = write
  return puffs
}

const _spriteBillboard = new Matrix4()
const _spriteSpin = new Matrix4()
const _spriteOffset = new Vector3()

/**
 * The instance matrix that draws one puff as a unit quad: `size`, then the
 * camera's rotation (a billboard, `m_RenderAlignment: 0` View), then the roll
 * about the facing axis, then the renderer's `m_Pivot` — the quad is shifted by
 * `pivot × size` along its **own** axes, so a spinning sprite swings around the
 * particle's position rather than turning on it. With a uniform scale the
 * order `S · B · R · T(pivot)` equals `B · R · T(pivot × size) · S`, which is
 * the picture Unity's pivot describes. ❓ The sign of the shift is §10.
 *
 * `cameraQuaternion` null leaves the quad flat, which is what a headless test
 * gets and still a position worth checking.
 */
export function puffSpriteMatrix(
  puff: Puff,
  spec: PuffSystemSpec,
  cameraQuaternion: Quaternion | null,
  out: Matrix4,
): Matrix4 {
  out.makeScale(puff.size, puff.size, puff.size)
  if (cameraQuaternion) out.multiply(_spriteBillboard.makeRotationFromQuaternion(cameraQuaternion))
  if (puff.rotation !== 0) out.multiply(_spriteSpin.makeRotationZ(puff.rotation))
  // `T(pivot)` on the right lands in the translation column, so it is folded
  // into the position rather than written after it: `out` has no translation
  // yet, so `applyMatrix4` is its 3×3 — the pivot turned with the sprite and
  // already multiplied by `size`.
  const [px, py, pz] = spec.pivot
  _spriteOffset.set(px, py, pz).applyMatrix4(out).add(puff.position)
  out.setPosition(_spriteOffset)
  return out
}

/** How long the whole thing lasts: the longest `lengthInSec` of the four. */
export const ICE_COUNT_PUFF_SECONDS = PUFF_SYSTEMS.reduce(
  (longest, spec) => Math.max(longest, spec.lengthSeconds),
  0,
)
